import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { format } from 'date-fns';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const RACE_CLASSES = [
  'Grade 1 Stakes',
  'Grade 2 Stakes',
  'Grade 3 Stakes',
  'Black Type Stakes',
  'Listed Stakes',
  'Restricted Stakes'
];

const RACE_RESULTS = ['Win', '2nd', '3rd'];

const RACE_SURFACES = ['Turf', 'Dirt', 'Synthetic'];

const RaceClassRecordDialog = ({ open, onClose, onSuccess, horse, record }) => {
  const [formData, setFormData] = useState({
    race_date: null,
    race_class: '',
    race_result: '',
    racetrack: '',
    race_name: '',
    race_purse_currency: 'USD',
    race_purse: '',
    race_distance: '',
    race_surface: '',
    age_at_race: '',
    other_race_notes: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (record) {
      setFormData({
        race_date: record.race_date ? new Date(record.race_date + (record.race_date.length === 10 ? 'T12:00:00' : '')) : null,
        race_class: record.race_class || '',
        race_result: record.race_result || '',
        racetrack: record.racetrack || '',
        race_name: record.race_name || '',
        race_purse_currency: record.race_purse_currency || 'USD',
        race_purse: record.race_purse || '',
        race_distance: record.race_distance || '',
        race_surface: record.race_surface || '',
        age_at_race: record.age_at_race || '',
        other_race_notes: record.other_race_notes || ''
      });
    } else {
      // Reset for new record
      setFormData({
        race_date: null,
        race_class: '',
        race_result: '',
        racetrack: '',
        race_name: '',
        race_purse_currency: 'USD',
        race_purse: '',
        race_distance: '',
        race_surface: '',
        age_at_race: calculateAge(),
        other_race_notes: ''
      });
    }
  }, [record, open]);

  const calculateAge = () => {
    if (!formData.race_date || !horse?.birthdate) return '';
    const raceDate = new Date(formData.race_date);
    const foalDate = new Date(horse.birthdate);
    let age = raceDate.getFullYear() - foalDate.getFullYear();
    const monthDiff = raceDate.getMonth() - foalDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && raceDate.getDate() < foalDate.getDate())) {
      age--;
    }
    return age;
  };

  useEffect(() => {
    if (formData.race_date && horse?.birthdate) {
      setFormData(prev => ({ ...prev, age_at_race: calculateAge() }));
    }
  }, [formData.race_date]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.race_date || !formData.race_class || !formData.race_result || !formData.racetrack || !formData.race_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        horse_id: horse.id,
        race_date: formData.race_date ? `${formData.race_date.getFullYear()}-${String(formData.race_date.getMonth()+1).padStart(2,'0')}-${String(formData.race_date.getDate()).padStart(2,'0')}` : null,
        race_class: formData.race_class,
        race_result: formData.race_result,
        racetrack: formData.racetrack,
        race_name: formData.race_name,
        race_purse_currency: formData.race_purse_currency,
        race_purse: formData.race_purse ? parseFloat(formData.race_purse) : null,
        race_distance: formData.race_distance || null,
        race_surface: formData.race_surface || null,
        age_at_race: formData.age_at_race ? parseInt(formData.age_at_race) : null,
        other_race_notes: formData.other_race_notes || null
      };

      if (record) {
        await api.put(`/race-class-records/${record.id}`, payload);
      } else {
        await api.post('/race-class-records', payload);
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving race class record:', error);
      toast.error('Failed to save race class record');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!record) return;
    setSaving(true);
    try {
      await api.delete(`/race-class-records/${record.id}`);
      onSuccess();
    } catch (error) {
      console.error('Error deleting race class record:', error);
      toast.error('Failed to delete race class record');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{record ? 'Edit' : 'New'} Race Class Record</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Horse - Display only */}
            <div className="space-y-2">
              <Label>Horse</Label>
              <Input value={horse?.system_name || ''} disabled />
            </div>

            {/* Age at Race - Auto-calculated */}
            <div className="space-y-2">
              <Label>Age at Race</Label>
              <Input
                value={formData.age_at_race}
                disabled
                placeholder="Auto-calculated"
              />
            </div>

            {/* Race Date */}
            <div className="space-y-2">
              <Label>Race Date *</Label>
              <Input
                type="date"
                value={formData.race_date ? format(formData.race_date, 'yyyy-MM-dd') : ''}
                onChange={(e) => {
                  if (e.target.value) {
                    setFormData({ ...formData, race_date: new Date(e.target.value + 'T12:00:00') });
                  } else {
                    setFormData({ ...formData, race_date: null });
                  }
                }}
                className="w-full"
              />
            </div>

            {/* Race Name */}
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.race_name}
                onChange={(e) => setFormData({ ...formData, race_name: e.target.value })}
                placeholder="Enter race name"
              />
            </div>

            {/* Race Class */}
            <div className="space-y-2">
              <Label>Race Class *</Label>
              <Select value={formData.race_class} onValueChange={(value) => setFormData({ ...formData, race_class: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select race class" />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  {RACE_CLASSES.map(cls => (
                    <SelectItem key={cls} value={cls}>{cls}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Place / Result */}
            <div className="space-y-2">
              <Label>Place *</Label>
              <Select value={formData.race_result} onValueChange={(value) => setFormData({ ...formData, race_result: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select result" />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  {RACE_RESULTS.map(result => (
                    <SelectItem key={result} value={result}>{result}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Race Purse */}
            <div className="space-y-2">
              <Label>Race Purse ({formData.race_purse_currency})</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.race_purse}
                onChange={(e) => setFormData({ ...formData, race_purse: e.target.value })}
                placeholder="Enter purse amount"
              />
            </div>

            {/* Race Track */}
            <div className="space-y-2">
              <Label>Race Track *</Label>
              <Input
                value={formData.racetrack}
                onChange={(e) => setFormData({ ...formData, racetrack: e.target.value })}
                placeholder="Enter track name"
              />
            </div>

            {/* Race Distance */}
            <div className="space-y-2">
              <Label>Race Distance</Label>
              <Input
                value={formData.race_distance}
                onChange={(e) => setFormData({ ...formData, race_distance: e.target.value })}
                placeholder="e.g., 1 mile, 1200m"
              />
            </div>

            {/* Race Surface */}
            <div className="space-y-2">
              <Label>Race Surface</Label>
              <Select value={formData.race_surface} onValueChange={(value) => setFormData({ ...formData, race_surface: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select surface" />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  {RACE_SURFACES.map(surface => (
                    <SelectItem key={surface} value={surface}>{surface}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Other Notes */}
          <div className="space-y-2">
            <Label>Other Notes</Label>
            <Textarea
              value={formData.other_race_notes}
              onChange={(e) => setFormData({ ...formData, other_race_notes: e.target.value })}
              placeholder="Additional notes about this race..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <div className="flex justify-between w-full">
              <div>
                {record && (
                  <Button type="button" variant="ghost" size="icon" onClick={handleDelete} disabled={saving} title="Delete record">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RaceClassRecordDialog;
